import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import {
  isGoLiveDateFieldVisible,
  isRepresentationValid,
  RepresentationDataType,
  RepresentationInput,
  validateRepresentation,
} from './representation-validation'

Locale.getInstance().registerKeySet('fr', 'lvc-create-event-add-representation', {
  eventTitle: 'Ajouter des représentations',
  exhibitionTitle: "Détails de l'exposition",
  festivalTitle: 'Détails de votre festival',
  eventSubtitle:
    'Remplissez tous les champs ci-dessous pour documenter la représentation de façon complète et fournir un maximum de détails utiles aux usagers de La Vitrine. Lorsque vous avez terminé, cliquez sur “suivant”.',
  exhibitionSubtitle:
    'Encore quelques détails qui nous aideront à mettre en avant votre exposition aux usagers de La Vitrine.',
  festivalSubtitle:
    'Remplissez tous les champs ci-dessous pour documenter votre festival de façon complète et fournir un maximum de détails utiles aux usagers de La Vitrine. Lorsque vous avez terminé, cliquez sur "suivant"',
  freeShowCheckboxLabel: 'Représentation gratuite',
  priceInputLabel: 'Prix*',
  priceTypeLabel: 'Type de prix*',
  priceTypeOptionsLabel: 'Prix minimum,Prix fixe',
  partOfFestivalLabel: "Cette représentation est dans le cadre d'un festival",
  expositionPartOfFestivalLabel: "Cette exposition est dans le cadre d'un festival*",
  nameOfFestivalLabel: 'Nom du festival*',
  exhibitionTypeLabel: "Type d'exposition*",
  exhibitionTypeOptionsLabel: 'Permanent,Temporaire',
  presenceModeLabel: 'Mode de présence*',
  presenceModeOptionsLabel: 'Sur place,En ligne,Hybride',
  physicalLocationTitle: 'Lieu physique',
  virtualLocationTitle: 'Lieu Virtuel',
  physicalLocationAddressLabel: 'Adresse*',
  physicalLocationCityLabel: 'Ville*',
  physicalLocationProvinceLabel: 'Province*',
  physicalLocationZipcodeLabel: 'Code postal*',
  virtualLocationUrl: 'URL de l’événement en ligne*',
  mainEntityOfPageFr: 'Site web du festival - Français',
  mainEntityOfPageEn: 'Site web du festival - Anglais',
  writeHerePlaceholder: 'Écrivez ici...',
  festivalSearchPlaceholder: 'Rechercher un festival',
  contributorsSubtitle:
    'Vous pouvez ajouter des contributeur·rice·s déjà existant·e·s ou en créer de nouveaux. À noter que les contributeur·rice·s ajoutés dans ce formulaire sont propres à cette représentation seulement.',
  errors: {
    startDate: 'Veuillez sélectionner une date de début et de fin',
    endDate: 'Veuillez sélectionner une date de début et de fin',
    startTime: 'Veuillez sélectionner une heure de début et de fin',
    endTime: 'Veuillez sélectionner une heure de début et de fin',
    startAndEndDateInvalid: 'La date de début ne peut être postérieure à la date de fin',
    startDateInPast: "La date de début ne peut être antérieure à aujourd'hui",
    languages: 'Veuillez sélectionner au moins une langue',
    invalidTicketingUrl: 'Veuillez saisir un lien de billetterie valide (http ou https)',
    publishDateAfterStart: 'La date de mise en ligne ne peut être postérieure à la date de début',
    startDateBeforePublishDate:
      "La date de début ne peut être antérieure à la date de mise en ligne choisie à l'étape précédente",
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-create-event-add-representation', {
  eventTitle: 'Add representations',
  exhibitionTitle: 'Exhibition details',
  festivalTitle: 'Detail of your festival',
  eventSubtitle:
    'Fill out the form below in order to supply us with a description of the performance as detailed as possible for La Vitrine users. When done, click “next”.',
  exhibitionSubtitle:
    'Only a few more details in order to assist us in showcasing your exhibition to La Vitrine users.',
  festivalSubtitle:
    "Fill in all the fields below to fully document your festival and provide La Vitrine users with as many useful details as possible. When you're finished, click “next”.",
  subtitle: 'Nullam potenti tortor neque quam nulla fermentum. Luctus.',
  freeShowCheckboxLabel: 'Free show',
  priceInputLabel: 'Price*',
  priceTypeLabel: 'Type of price*',
  priceTypeOptionsLabel: 'Minimum price,Fixed price',
  partOfFestivalLabel: 'This performance is part of a festival',
  expositionPartOfFestivalLabel: 'This exposition is part of a festival*',
  nameOfFestivalLabel: 'Festival name*',
  exhibitionTypeLabel: 'Type of exhibition*',
  exhibitionTypeOptionsLabel: 'Permanent,Temporary',
  presenceModeLabel: 'Presence mode*',
  presenceModeOptionsLabel: 'In person,Online,Hybrid',
  physicalLocationTitle: 'Physical location',
  virtualLocationTitle: 'Virtual location',
  physicalLocationAddressLabel: 'Address*',
  physicalLocationCityLabel: 'City*',
  physicalLocationProvinceLabel: 'Province*',
  physicalLocationZipcodeLabel: 'Postal code*',
  virtualLocationUrl: 'URL of the online event*',
  mainEntityOfPageFr: 'Website of festival - French',
  mainEntityOfPageEn: 'Website of festival - English',
  writeHerePlaceholder: 'Write here...',
  festivalSearchPlaceholder: 'Search for a festival',
  contributorsSubtitle:
    'You can add existing contributors or create new ones. Contributors added in this form are only associated to this offer specifically.',
  errors: {
    startDate: 'Please select a start date and end date',
    endDate: 'Please select a start date and end date',
    startTime: 'Please select a start time and end time',
    endTime: 'Please select a start time and end time',
    startAndEndDateInvalid: 'Start date cannot be later than end date',
    startDateInPast: 'The start date cannot be earlier than today',
    languages: 'Please select at least one language',
    invalidTicketingUrl: 'Please enter a valid ticketing link (http or https)',
    publishDateAfterStart: 'The publication date cannot be later than the start date',
    startDateBeforePublishDate:
      'The start date cannot be earlier than the publication date chosen in the previous step',
  },
})

export interface LvcCreateEventAddRepresentation {
  errors: any
  values: { [id: string]: string | boolean | Date | Date[] }
  dataType: string
  representations: Array<IRepresentation>
  index: any
  errorValues: { [key: string]: string }
  flowStep: string
  flowGroup: string
}

export interface IRepresentation {
  startDate: string
  endDate: string
  presenceMode: string
  reservationRequired: any
  exhibitionType: string
  freeShow: any
  price: string
  priceType: string
  partOfFestival: any
  festival: string
  festivalId?: number
  address: string
  addressLine2?: string
  city: string
  province: string
  zipcode: string
  virtualLocationUrl: string
  place: string
  placeData: any
  virtualPlace: string
  virtualPlaceData: any
  dateOfSale: string
  startTime: string
  endTime: string
  ticketingUrl: string
  languages: any
  mainEntityOfPageFr?: string
  mainEntityOfPageEn?: string
  contributors?: any[]
}

const createRepresentationInitialState: IRepresentation = {
  startDate: '',
  endDate: '',
  presenceMode: 'onsite',
  reservationRequired: true,
  exhibitionType: '',
  freeShow: false,
  price: '',
  priceType: '',
  partOfFestival: false,
  festival: '',
  address: '',
  addressLine2: '',
  city: '',
  province: '',
  zipcode: '',
  virtualLocationUrl: '',
  place: '',
  placeData: null,
  virtualPlace: '',
  virtualPlaceData: null,
  dateOfSale: '',
  startTime: '',
  endTime: '',
  ticketingUrl: '',
  languages: null,
  contributors: [],
}

export interface AddRepresentationData {
  representations: IRepresentation[]
}

const EXHIBITION_TYPE_OPTIONS = ['permanent', 'temporary'].join(',')
const PRESENCE_MODE_OPTIONS = ['onsite', 'online', 'hybrid'].join(',')
const PRICE_TYPE_OPTIONS = ['minimum-price', 'fixed-price'].join(',')
const ZIPCODE_MASK = 'A#A #A#'

export class LvcCreateEventAddRepresentation
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  // Field-specific callbacks for form inputs
  private onChangeExhibitionTypeCb: string = ''
  private onChangeFreeShowCb: string = ''
  private onChangePriceTypeCb: string = ''
  private onChangePriceCb: string = ''
  private onChangePartOfFestivalCb: string = ''
  private onChangePresenceModeCb: string = ''
  private onChangeVirtualLocationUrlCb: string = ''
  private onChangeMainEntityOfPageFrCb: string = ''
  private onChangeMainEntityOfPageEnCb: string = ''
  private onChangePlaceCb: string = ''
  private onDatesChangeCb: string = ''
  private onStartTimeChangeCb: string = ''
  private onEndTimeChangeCb: string = ''
  private onTicketingUrlChangeCb: string = ''
  private onDateOfSaleChange: string = ''
  private onReservationRequiredChange: string = ''
  private onPlaceChange: string = ''
  private onVirtualPlaceChange: string = ''
  private onFestivalChange: string = ''
  private onLanguagesChange: string = ''
  private onContributorsChange: string = ''

  errorValues = {
    startDate: Locale.getKey('lvc-create-event-add-representation.errors.startDate'),
    endDate: Locale.getKey('lvc-create-event-add-representation.errors.endTime'),
    startTime: Locale.getKey('lvc-create-event-add-representation.errors.startTime'),
    endTime: Locale.getKey('lvc-create-event-add-representation.errors.endTime'),
    languages: Locale.getKey('lvc-create-event-add-representation.errors.languages'),
    exhibitionType: Locale.getKey('general.required'),
    price: Locale.getKey('general.required'),
    priceType: Locale.getKey('general.required'),
    festival: Locale.getKey('general.required'),
    presenceMode: Locale.getKey('general.required'),
    place: Locale.getKey('general.required'),
    address: Locale.getKey('general.required'),
    city: Locale.getKey('general.required'),
    province: Locale.getKey('general.required'),
    zipcode: Locale.getKey('general.required'),
    virtualPlace: Locale.getKey('general.required'),
    virtualLocationUrl: Locale.getKey('general.required'),
    ticketingUrl: Locale.getKey('general.required'),
    dateOfSale: Locale.getKey('general.required'),
  } as { [key: string]: string }

  attrs: Attrs = [
    ['data-type', 'event'],
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
    ['index', ''],
    ['enable-contributors', false],
  ]

  reactive: Reactive = [
    ['errors', {}],
    [
      'values',
      {
        presenceMode: 'onsite',
        reservationRequired: true,
      },
    ],
    ['representations', []],
    ['index', ''],
    [
      'errors',
      {
        startDate: false,
        endDate: false,
        startTime: false,
        endTime: false,
      },
      false,
    ],
  ]

  static observedAttributes = ['index']

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ) {
    if (name === 'index' && newValue !== oldValue) {
      this.updateRepresentationIndex()
    }
  }

  updateRepresentationIndex() {
    if (this.index && this.index !== true) {
      this.index = parseInt(this.index)
    }
    this.rerender()
  }

  private createFieldHandler(field: string) {
    return (event: string | boolean | number | Event | React.ChangeEvent<any>) => {
      // Extract value from event if it's an Event object
      let value: string | boolean | number

      if (
        event instanceof Event ||
        (typeof event === 'object' && event !== null && 'target' in event)
      ) {
        // It's an Event Object (native or React event)
        const target = (event as any).target
        value = target.type === 'checkbox' ? target.checked : target.value
      } else {
        // It's a direct value (string, boolean, number)
        value = event
      }

      // Safety check for valid index
      if (this.index === undefined || this.representations[this.index] === undefined) {
        console.warn('Invalid index or representation not initialized')
        return
      }

      // Create a copy of the current state
      const currentState = { ...this.representations[this.index] }
      const updates: { [key: string]: any } = {}

      // Update the field value
      updates[field] = value

      // Handle special case: free show
      if (field === 'freeShow' && value === true) {
        updates.price = ''
        updates.priceType = ''
      }

      // Handle special case: presence mode change
      if (field === 'presenceMode' && currentState.presenceMode !== value) {
        const locationResets = {
          place: '',
          placeData: null,
          virtualPlace: '',
          virtualPlaceData: null,
          address: '',
          city: '',
          province: '',
          zipcode: '',
          virtualLocationUrl: '',
        }

        Object.assign(updates, locationResets)
      }

      // Update state and rerender
      this.representations[this.index] = {
        ...currentState,
        ...updates,
      }

      // Force synchronous render to ensure UI is updated
      this.rerender()
    }
  }

  onChangeDates(value: { [key: string]: Date[] }) {
    this.representations[this.index] = {
      ...this.representations[this.index],
      ...value,
    }

    this.rerender()
  }

  onStartTimeChange(value: string) {
    this.representations[this.index].startTime = value
    this.rerender()
  }
  onEndTimeChange(value: string) {
    this.representations[this.index].endTime = value
    this.rerender()
  }

  onChangeTicketingUrl(value: string) {
    this.representations[this.index].ticketingUrl = value
    this.rerender()
  }

  onChangeReservationRequired(value: string | boolean) {
    this.representations[this.index].reservationRequired = value === 'true' || value === true
    this.rerender()
  }

  onChangeDateOfSale(value: string) {
    this.representations[this.index].dateOfSale = value
    this.rerender()
  }

  onChangePlace(data: { value: string; element: HTMLElement; place?: any }) {
    this.representations[this.index].place = data.value
    this.representations[this.index].placeData = data.place || ''
    this.representations[this.index].address = data.place?.streetAddress || ''
    this.representations[this.index].city = data.place?.addressLocality || ''
    this.representations[this.index].province = data.place?.addressRegion || ''
    this.representations[this.index].zipcode = data.place?.postalCode || ''

    this.rerender(true)
  }

  onChangeVirtualPlace(data: { value: string; element: HTMLElement; place?: any }) {
    this.representations[this.index].virtualPlace = data.value
    this.representations[this.index].virtualPlaceData = data.place || ''
    this.representations[this.index].virtualLocationUrl = data.place?.urlFr || ''

    this.rerender()
  }

  // this function receives the data from the address autofill component
  // after the user selects a suggestion from the search box
  onChangeAddressAutocomp(data: any) {
    if (!data) return

    // Map the new data structure to the corresponding properties
    this.representations[this.index].address = data['street-address'] || ''
    this.representations[this.index].addressLine2 = data['address-line2'] || ''
    this.representations[this.index].city = data['address-level2'] || ''
    this.representations[this.index].province = data['address-level1'] || ''
    this.representations[this.index].zipcode = data['postal-code'] || ''
  }

  onChangeFestival(data: any) {
    if (!data) return
    this.representations[this.index].festival = data.isNew
      ? data.value
      : Locale.language === 'en'
        ? data?.[0]?.nameEn
        : data?.[0]?.nameFr
    this.representations[this.index].festivalId = data.isNew ? null : data?.[0]?.id || null

    this.rerender(true)
  }

  onChangeLanguages(data: any) {
    this.representations[this.index].languages = data
    this.rerender()
  }

  onChangeContributors(event: any) {
    this.representations[this.index].contributors = event.detail.contributors
    this.rerender()
  }

  onReady() {
    // Create field-specific callbacks using the factory
    this.onChangeExhibitionTypeCb = Callback.attach(this.createFieldHandler('exhibitionType'))
    this.onChangeFreeShowCb = Callback.attach(this.createFieldHandler('freeShow'))
    this.onChangePriceTypeCb = Callback.attach(this.createFieldHandler('priceType'))
    this.onChangePriceCb = Callback.attach(this.createFieldHandler('price'))
    this.onChangePartOfFestivalCb = Callback.attach(this.createFieldHandler('partOfFestival'))
    this.onChangePresenceModeCb = Callback.attach(this.createFieldHandler('presenceMode'))
    this.onChangeVirtualLocationUrlCb = Callback.attach(
      this.createFieldHandler('virtualLocationUrl')
    )
    this.onChangeMainEntityOfPageFrCb = Callback.attach(
      this.createFieldHandler('mainEntityOfPageFr')
    )
    this.onChangeMainEntityOfPageEnCb = Callback.attach(
      this.createFieldHandler('mainEntityOfPageEn')
    )
    this.onStartTimeChangeCb = Callback.attach(this.onStartTimeChange.bind(this))
    this.onEndTimeChangeCb = Callback.attach(this.onEndTimeChange.bind(this))
    this.onDatesChangeCb = Callback.attach(this.onChangeDates.bind(this))
    this.onTicketingUrlChangeCb = Callback.attach(this.onChangeTicketingUrl.bind(this))
    this.onReservationRequiredChange = Callback.attach(this.onChangeReservationRequired.bind(this))
    this.onPlaceChange = Callback.attach(this.onChangePlace.bind(this))
    this.onVirtualPlaceChange = Callback.attach(this.onChangeVirtualPlace.bind(this))
    this.onChangeAddressAutocompCb = Callback.attach(this.onChangeAddressAutocomp.bind(this))

    this.onDateOfSaleChange = Callback.attach(this.onChangeDateOfSale.bind(this))
    this.onFestivalChange = Callback.attach(this.onChangeFestival.bind(this))
    this.onLanguagesChange = Callback.attach(this.onChangeLanguages.bind(this))
    this.onContributorsChange = Callback.attach(this.onChangeContributors.bind(this))

    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    //this.representations = [...this.representations, { ...createRepresentationInitialState }];
    this.rerender()
  }

  onDestroy(): void {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  async deserialize(stepData?: any, data?: unknown): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const d = (stepData as AddRepresentationData) ?? {}
    const entireEventData = data as any
    if (entireEventData.control_currentRepresentationStep) {
      this.index = entireEventData.control_currentRepresentationStep
    }

    // The event-level "date de mise en ligne" (uploadDate) is not persisted at
    // the event level, so use it to pre-fill the first representation's go-live
    // date — that is what actually drives the offer's datePublished.
    const eventGoLiveDate =
      this.dataType === 'exhibition'
        ? entireEventData?.createExhibitionDescription?.uploadDate
        : entireEventData?.createEventDescription?.uploadDate

    this.representations = d.representations || []

    if (
      this.dataType !== 'exhibition' &&
      (!this.index || this.index === true || this.index === '')
    ) {
      this.index = this.representations?.length
      // push a new representation with default values; the first one inherits
      // the event-level go-live date when the user chose one.
      this.representations[this.index] = {
        ...createRepresentationInitialState,
        ...(this.index === 0 && eventGoLiveDate ? { dateOfSale: eventGoLiveDate } : {}),
      }
    }
    if (this.dataType === 'exhibition') {
      this.index = 0
      if (entireEventData?.createExhibitionDetails?.representations) {
        this.representations = entireEventData.createExhibitionDetails.representations
      } else {
        this.representations[this.index] = {
          ...createRepresentationInitialState,
          ...(eventGoLiveDate ? { dateOfSale: eventGoLiveDate } : {}),
        }
      }
    }
    if (this.dataType === 'festival') {
      this.index = 0
      if (entireEventData?.createEventDetails?.representations) {
        this.representations = entireEventData.createEventDetails.representations
      } else {
        this.representations[this.index] = {
          ...createRepresentationInitialState,
        }
      }
    }

    this.rerender()
  }

  public async serialize(skipValidation = false): Promise<unknown> {
    let flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    if (!flowCore) {
      return
    }

    flowCore.getData().control_currentRepresentationStep = undefined

    // If we're just autosaving, return all representations without validation
    if (skipValidation || flowCore.getSaveState() === 'saving') {
      return {
        representations: [...this.representations],
      }
    }

    // For normal navigation (next/prev), validate representations
    const representations = [...this.representations]
    const validRepresentations = []
    for (const representation of representations) {
      const index = representations.indexOf(representation)
      const isValid = await this.validate(index)
      if (isValid) validRepresentations.push(representation)
    }

    return {
      representations: validRepresentations,
    }
  }

  async validate(index?: number): Promise<boolean> {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    let representationIndex = index || index === 0 ? index : this.index
    const values = this.representations[representationIndex]
    if (!values) return false

    // Delegate all rule logic to the shared, unit-tested validator so the
    // per-step check and the final submission gate stay in sync (PRO-48).
    const result = validateRepresentation(
      values as unknown as RepresentationInput,
      this.dataType as RepresentationDataType
    )

    result.missingFields.forEach(fieldKey => {
      this.errors[fieldKey] = this.errorValues[fieldKey] || Locale.getKey('general.required')
    })
    if (result.endBeforeStart) {
      this.errors['endDate'] = Locale.getKey(
        'lvc-create-event-add-representation.errors.startAndEndDateInvalid'
      )
    }
    if (result.startInPast) {
      this.errors['startDate'] = Locale.getKey(
        'lvc-create-event-add-representation.errors.startDateInPast'
      )
    }
    if (result.invalidTicketingUrl) {
      this.errors['ticketingUrl'] = Locale.getKey(
        'lvc-create-event-add-representation.errors.invalidTicketingUrl'
      )
    }
    if (result.publishDateAfterStart) {
      this.errors['dateOfSale'] = Locale.getKey(
        'lvc-create-event-add-representation.errors.publishDateAfterStart'
      )
      if (!isGoLiveDateFieldVisible(values as unknown as RepresentationInput)) {
        this.errors['startDate'] = Locale.getKey(
          'lvc-create-event-add-representation.errors.startDateBeforePublishDate'
        )
      }
    }

    const isValid = isRepresentationValid(result)

    // if we have errors, go to the top of the page
    // and show generic error message
    if (!isValid) {
      window.scrollTo(0, 0)
      this.errors.generic = Locale.getKey('create-event-description.errors.generic')
    }
    this.rerender()
    return isValid
  }

  async submit() {
    this.errors = {}

    // Safety check for valid index
    if (this.index === undefined || this.representations[this.index] === undefined) {
      console.warn('Invalid index or representation not initialized')
      return
    }

    // Get the FlowCore instance to save the data
    const flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    if (flowCore) {
      // Validate before saving
      if (await this.validate(this.index)) {
        await flowCore.save()
      }
    } else {
      console.warn('FlowCore instance not found, Saving aborted.')
    }
  }

  html = () => `
    <lvc-box>
      <lvc-box mb="16px">
        <lvc-text font-preset="titleMobileSmall">${Locale.getKey(
          `lvc-create-event-add-representation.${this.dataType}Title`
        )}</lvc-text>
      </lvc-box>
      <lvc-box>
        <lvc-text mt="16px" font-preset="medium">${Locale.getKey(
          `lvc-create-event-add-representation.${this.dataType}Subtitle`
        )}</lvc-text>
      </lvc-box>
    </lvc-box>

    <lvc-box mt="12px">
      <lvc-error error="${this.errors.generic}"></lvc-error>
    </lvc-box>

    <lvc-box mt="40px">
      ${
        this.dataType === 'exhibition'
          ? /*html*/ `
          <lvc-box mb="16px">
            <lvc-select
		          id="exhibitionType"
		          name="exhibitionType"
		          onChange="${this.onChangeExhibitionTypeCb}"
		          default-value="${this.representations[this.index]?.exhibitionType || ''}"
              error="${this.errors.exhibitionType || ''}"
		          label="${Locale.getKey('lvc-create-event-add-representation.exhibitionTypeLabel')}"
		          placeholder="${Locale.getKey('general.choose')}"
		          selectOptions="${EXHIBITION_TYPE_OPTIONS}"
		          selectOptionLabels="${Locale.getKey(
                'lvc-create-event-add-representation.exhibitionTypeOptionsLabel'
              )}"
		          theme="outline"
		          height="48px"
		          fontWeight="${Font.weight.regular}"
		        ></lvc-select>
          </lvc-box>
        `
          : ''
      }
      <lvc-box display="flex" columnGap="16px">

        ${
          this.dataType === 'exhibition' &&
          this.representations[this.index]?.['exhibitionType'] === 'permanent'
            ? ''
            : /*html*/ `
          <lvc-box>
            <lvc-date-range-input-field
              startDate="${this.representations[this.index]?.startDate || ''}"
              endDate="${this.representations[this.index]?.endDate || ''}"
              on-change="${this.onDatesChangeCb}"
            ></lvc-date-range-input-field>
            ${
              this.errors.startDate || this.errors.endDate
                ? `<lvc-error error="${this.errors.startDate || this.errors.endDate}"></lvc-error>`
                : ``
            }
          </lvc-box>
        `
        }
        ${
          this.dataType === 'event'
            ? /*html*/ `
          <lvc-box>
            <lvc-start-end-time-input-field
              startTime="${this.representations[this.index]?.startTime || ''}"
              endTime="${this.representations[this.index]?.endTime || ''}"
              onChangeStartTime="${this.onStartTimeChangeCb}"
              onChangeEndTime="${this.onEndTimeChangeCb}"
            ></lvc-start-end-time-input-field>
            ${
              this.errors.startTime || this.errors.endTime
                ? `<lvc-error error="${this.errors.startTime || this.errors.endTime}"></lvc-error>`
                : ``
            }
          </lvc-box>
        `
            : ''
        }
      </lvc-box>
      <lvc-box mt="24px" height="2px" background="#1E007D4D"></lvc-box>
    </lvc-box>
    ${
      this.dataType === 'event'
        ? /*html*/ `
      <lvc-box mt="16px">
        <lvc-languages-select-field
          on-change="${this.onLanguagesChange}"
          error="${this.errors.languages || ''}"
          default-value="${
            encodeURIComponent(JSON.stringify(this.representations[this.index]?.['languages'])) ||
            ''
          }"
          value="${
            encodeURIComponent(JSON.stringify(this.representations[this.index]?.['languages'])) ||
            ''
          }"
        ></lvc-languages-select-field>
        <lvc-box height="2px" mt="16px" background="#1E007D4D"></lvc-box>
      </lvc-box>
    `
        : ''
    }
    <lvc-box mt="16px">
      <lvc-checkbox
        name="freeShow"
        on-change="${this.onChangeFreeShowCb}"
        default-value="${this.representations[this.index]?.freeShow || 'false'}"
        checked="${this.representations[this.index]?.freeShow || 'false'}"
        secondary-style="true"
        label="${Locale.getKey('lvc-create-event-add-representation.freeShowCheckboxLabel')}"
      ></lvc-checkbox>
      ${
        !this.representations[this.index]?.freeShow
          ? /*html*/ `
        <lvc-box mt="16px" display="flex" columnGap="16px">
          <lvc-select
		        id="priceType"
		        name="priceType"
		        onChange="${this.onChangePriceTypeCb}"
		        default-value="${this.representations[this.index]?.priceType || ''}"
            error="${this.errors.priceType || ''}"
            placeholder="${Locale.getKey('general.choose')}"
		        label="${Locale.getKey('lvc-create-event-add-representation.priceTypeLabel')}"
		        selectOptions="${PRICE_TYPE_OPTIONS}"
		        selectOptionLabels="${Locale.getKey(
              'lvc-create-event-add-representation.priceTypeOptionsLabel'
            )}"
		        theme="outline"
		        height="48px"
		        fontWeight="${Font.weight.regular}"
		      ></lvc-select>
          <lvc-input
            secondary-style="true"
            name="price"
            type="number"
            on-change="${this.onChangePriceCb}"
            default-value="${this.representations[this.index]?.price || ''}"
            error="${this.errors.price || ''}"
            placeholder="${Locale.getKey(
              'lvc-create-event-add-representation.writeHerePlaceholder'
            )}"
            label="${Locale.getKey('lvc-create-event-add-representation.priceInputLabel')}"
          ></lvc-input>
        </lvc-box>
      `
          : ''
      }
      <lvc-box height="2px" mt="16px" background="#1E007D4D"></lvc-box>
    </lvc-box>
    ${
      this.dataType !== 'festival'
        ? /*html*/ `
      <lvc-box mt="16px">
        <lvc-checkbox
          name="partOfFestival"
          on-change="${this.onChangePartOfFestivalCb}"
          default-value="${this.representations[this.index]?.partOfFestival || 'false'}"
          error="${this.errors.partOfFestival || 'false'}"
          secondary-style="true"
          label="${Locale.getKey(
            this.dataType === 'event'
              ? 'lvc-create-event-add-representation.partOfFestivalLabel'
              : 'lvc-create-event-add-representation.expositionPartOfFestivalLabel'
          )}"
        ></lvc-checkbox>
            ${
              this.representations[this.index]?.partOfFestival
                ? /*html*/ `
            <lvc-box mt="16px">
              <lvc-event-search-field
                default-value="${this.representations[this.index]?.festival || ''}"
                title="${Locale.getKey('lvc-create-event-add-representation.nameOfFestivalLabel')}"
                placeholder="${Locale.getKey(
                  'lvc-create-event-add-representation.festivalSearchPlaceholder'
                )}"
                events-type="festivals"
                on-change="${this.onFestivalChange}"
                disable-creation="true"
              ></lvc-event-search-field>
            </lvc-box>
            ${this.errors.festival ? `<lvc-error error="${this.errors.festival}"></lvc-error>` : ``}
            `
                : ''
            }
        <lvc-box height="2px" mt="16px" background="#1E007D4D"></lvc-box>
      </lvc-box>
    `
        : ''
    }
    <lvc-box mt="24px">
      <lvc-box mb="24px">
		    <lvc-select
		      id="presenceMode"
		      name="presenceMode"
		      onChange="${this.onChangePresenceModeCb}"
		      default-value="${this.representations[this.index]?.presenceMode}"
          error="${this.errors.presenceMode || ''}"
		      label="${Locale.getKey('lvc-create-event-add-representation.presenceModeLabel')}"
		      selectOptions="${PRESENCE_MODE_OPTIONS}"
		      selectOptionLabels="${Locale.getKey(
            'lvc-create-event-add-representation.presenceModeOptionsLabel'
          )}"
		      theme="outline"
		      height="48px"
		      fontWeight="${Font.weight.regular}"
		    ></lvc-select>
		  </lvc-box>
		  <lvc-box>
		    <lvc-reservation-required-block
		      reservation-required="${this.representations[this.index]?.reservationRequired}"
		      paid="${!this.representations[this.index]?.freeShow}"
		      on-reservation-required-change="${this.onReservationRequiredChange}"
		      ticketing-url="${this.representations[this.index]?.ticketingUrl || 'null'}"
		      on-ticketing-url-change="${this.onTicketingUrlChangeCb}"
		      ticketing-url-error="${this.errors.ticketingUrl}"
		      date-of-sale="${this.representations[this.index]?.dateOfSale || ''}"
          date-of-sale-error="${this.errors.dateOfSale}"
          on-date-of-sale-change="${this.onDateOfSaleChange}"
		    ></lvc-reservation-required-block>
		  </lvc-box>
    </lvc-box>
    ${
      this.representations[this.index]?.presenceMode === 'onsite' ||
      this.representations[this.index]?.presenceMode === 'hybrid'
        ? /*html*/ `
        <lvc-box>
          <lvc-box mb="16px">
            <lvc-text font-preset="medium">${Locale.getKey(
              'lvc-create-event-add-representation.physicalLocationTitle'
            )}</lvc-text>
          </lvc-box>
          <lvc-box mb="16px">
            <lvc-location-search-field
              default-value="${this.representations[this.index]?.place || ''}"
              on-change="${this.onPlaceChange}"
              error="${this.errors.place || ''}"
            ></lvc-location-search-field>
          </lvc-box>
          ${
            this.representations[this.index]?.place
              ? /*html*/ `
            <lvc-box mb="16px">
              <lvc-mapbox-address-autofill
              on-change="${this.onChangeAddressAutocompCb}"
              place-address="${
                this.representations[this.index]?.address !== ''
                  ? this.representations[this.index]?.address
                  : null
              }"
              place-address-line2="${
                this.representations[this.index]?.addressLine2 !== ''
                  ? this.representations[this.index]?.addressLine2
                  : null
              }"
              place-city="${
                this.representations[this.index]?.city !== ''
                  ? this.representations[this.index]?.city
                  : null
              }"
              place-province="${
                this.representations[this.index]?.province !== ''
                  ? this.representations[this.index]?.province
                  : null
              }"
              place-zipcode="${
                this.representations[this.index]?.zipcode !== ''
                  ? this.representations[this.index]?.zipcode
                  : null
              }"
              ></lvc-mapbox-address-autofill>
            </lvc-box>

          `
              : ''
          }
          ${
            this.representations[this.index]?.presenceMode === 'hybrid'
              ? `<lvc-box height="2px" background="#1E007D4D"></lvc-box>`
              : ''
          }
        </lvc-box>
      `
        : ''
    }
    ${
      this.representations[this.index]?.presenceMode === 'online' ||
      this.representations[this.index]?.presenceMode === 'hybrid'
        ? /*html*/ `
        <lvc-box mt="24px">

          <lvc-box mb="16px">
            <lvc-text font-preset="medium">${Locale.getKey(
              'lvc-create-event-add-representation.virtualLocationTitle'
            )}</lvc-text>
          </lvc-box>
          <lvc-box mb="16px">
            <lvc-location-search-field
              default-value="${this.representations[this.index]?.virtualPlace || ''}"
              on-change="${this.onVirtualPlaceChange}"
              error="${this.errors.virtualPlace || ''}"
              virtual="true"
            ></lvc-location-search-field>
          </lvc-box>
          ${
            this.representations[this.index]?.virtualPlace
              ? /*html*/ `
                <lvc-box mb="16px">
                  <lvc-input
                    secondary-style="true"
                    name="virtualLocationUrl"
                    on-change="${this.onChangeVirtualLocationUrlCb}"
                    default-value="${this.representations[this.index]?.virtualLocationUrl || ''}"
                    error="${this.errors.virtualLocationUrl || ''}"
                    placeholder="${Locale.getKey(
                      'lvc-create-event-add-representation.writeHerePlaceholder'
                    )}"
                    label="${Locale.getKey(
                      'lvc-create-event-add-representation.virtualLocationUrl'
                    )}"
                    disabled="${!!this.representations[this.index]?.virtualPlaceData || false}"
                  ></lvc-input>
                </lvc-box>
              `
              : ''
          }
        </lvc-box>
      `
        : ''
    }
    ${
      this.dataType === 'festival'
        ? /*html*/ `
      <div class="separator"></div>
      <lvc-box mt="24px">
        <lvc-input
          secondary-style="true"
          on-change="${this.onChangeMainEntityOfPageFrCb}"
          default-value="${this.representations[this.index]?.mainEntityOfPageFr || ''}"
          border="1px solid #1E007D"
          id="mainEntityOfPageFr"
          placeholder="${Locale.getKey('create-event-description.placeholder')}"
          name="mainEntityOfPageFr"
          error="${this.errors.mainEntityOfPageFr || ''}"
          label="${Locale.getKey('lvc-create-event-add-representation.mainEntityOfPageFr')}"
        ></lvc-input>
      </lvc-box>
      <lvc-box mt="24px">
        <lvc-input
          secondary-style="true"
          on-change="${this.onChangeMainEntityOfPageEnCb}"
          default-value="${this.representations[this.index]?.mainEntityOfPageEn || ''}"
          border="1px solid #1E007D"
          id="mainEntityOfPageEn"
          placeholder="${Locale.getKey('create-event-description.placeholder')}"
          name="mainEntityOfPageEn"
          error="${this.errors.mainEntityOfPageEn}"
          label="${Locale.getKey('lvc-create-event-add-representation.mainEntityOfPageEn')}"
        ></lvc-input>
      </lvc-box>
    `
        : ''
    }
    ${
      this.enableContributors
        ? /*html*/ `
      <div class="separator"></div>
      <lvc-box mt="24px">
        <lvc-box mb="16px">
          <lvc-text font-preset="titleMobileSmall">${Locale.getKey(
            'create-event-add-contributors.title'
          )}</lvc-text>
        </lvc-box>
        <lvc-box mb="24px">
          <lvc-text font-preset="medium">${Locale.getKey(
            'lvc-create-event-add-representation.contributorsSubtitle'
          )}</lvc-text>
        </lvc-box>
        <lvc-contributors-selector
          default-value="${encodeURIComponent(
            JSON.stringify(this.representations[this.index]?.contributors || [])
          )}"
          on-change="${this.onContributorsChange}"
        ></lvc-contributors-selector>
      </lvc-box>
    `
        : ''
    }
  `

  css = () => `
    lvc-input[disabled=true] {
      opacity: 0.5
    }
    .separator {
      height: 2px; 
      background-color: #1E007D4D;
      margin: 24px 0px ;
    }
  `

  eventListeners: EventListeners = [
    ['#submit', 'click', this.submit.bind(this)],
    [
      'lvc-contributors-selector',
      'contributors-selector:change',
      this.onChangeContributors.bind(this),
    ],
  ]
}
